import { useRecoilValue } from "recoil";

import {
  filterOnlyDirty,
  hookFormContextAF,
  Log,
  NEW_RIDE_ADDITIONAL_ALWAYS_DIRTY_FIELDS,
  RIDE_ALWAYS_DIRTY_FIELDS,
  RideCrudModeKey,
  type IRideDto,
  type IRideMetadata,
} from "@ryde/shared";

import { applyDirtyEditableFormMetadata } from "./metadata-utils";

export const useRideFormFields = () => {
  const rideFormContext = useRecoilValue(
    hookFormContextAF(RideCrudModeKey.uiUnit),
  );

  const prepareFormFieldsData = (
    formValuesSnapshot: Partial<IRideDto> | undefined,
    isDuplicateForm: boolean | undefined,
    isEdit: boolean | undefined,
  ): Partial<IRideDto> => {
    if (!formValuesSnapshot) {
      return {};
    }

    const { metadata, ...formValuesSnapshotExcludeMetadata } =
      formValuesSnapshot;

    if (isDuplicateForm) {
      return formValuesSnapshotExcludeMetadata;
    }

    const dirtyFieldsComputed: Partial<
      Record<keyof IRideDto, number | boolean | undefined | object>
    > = {
      ...rideFormContext?.dirtyFields,
      ...RIDE_ALWAYS_DIRTY_FIELDS,
      ...(isEdit ? {} : NEW_RIDE_ADDITIONAL_ALWAYS_DIRTY_FIELDS),
    };

    if (metadata?.keepSupplier && formValuesSnapshot.supplierId) {
      dirtyFieldsComputed.supplierId = true;
    }
    if (!isEdit && formValuesSnapshot.journeyId) {
      dirtyFieldsComputed.journeyId = formValuesSnapshot.journeyId;
    }

    Log.debug(
      "Ride form dirty fields original => computed:",
      rideFormContext?.dirtyFields,
      dirtyFieldsComputed,
    );

    const formDataCleaned = filterOnlyDirty({
      formFields: formValuesSnapshotExcludeMetadata as Partial<IRideDto>,
      dirtyFields: dirtyFieldsComputed,
      skipMetadata: true,
    });
    Log.debug("Ride form plain data cleaned for api send:", formDataCleaned);
    return formDataCleaned;
  };

  const mergeFormValuesToMetadata = (
    formValuesSnapshot: Partial<IRideDto> | undefined,
    metadataComputed: IRideMetadata,
  ): void => {
    applyDirtyEditableFormMetadata({
      formValuesSnapshot,
      dirtyMetadataFields: rideFormContext?.dirtyFields?.metadata,
      metadataComputed,
    });
  };

  return { mergeFormValuesToMetadata, prepareFormFieldsData };
};
